/**
 * VirtualDJ database.xml parser.
 *
 * VirtualDJ stores its library at:
 *   macOS   ~/Documents/VirtualDJ/database.xml
 *   Windows %LOCALAPPDATA%\VirtualDJ\database.xml
 *   Linux   ~/.VirtualDJ/database.xml
 *
 * XML shape (VirtualDJ 8 / 2021 / 2024 — backwards-compat):
 *   <VirtualDJ_Database>
 *     <Song FilePath="/Users/dj/Music/Fisher/Losing It.mp3" FileSize="9814032">
 *       <Tags Author="Fisher" Title="Losing It" Album="Losing It EP"
 *             Genre="Tech House" Year="2018" Bpm="127.5" Key="Cm"/>
 *       <Infos SongLength="245.023"/>
 *       <Poi Pos="0.253"     Type="cue"    Num="0" Name="Intro"/>
 *       <Poi Pos="61.5"      Type="cue"    Num="1" Name="Drop"/>
 *       <Poi Pos="122.0"     Type="loop"   Size="8"/>
 *       <Scan Bpm="127.500" AltBpm="0" FirstBeat="0.253"/>
 *     </Song>
 *     ...
 *   </VirtualDJ_Database>
 *
 * Cross-parser normalisation:
 *   - Poi `Pos` is already in **seconds** (float, matches Rekordbox — unlike
 *     Traktor which uses ms).
 *   - Poi `Type="cue"` with `Num` 0..7 = hot cue. Other types (loop, saved
 *     effect) are skipped to keep parity with Serato / Rekordbox / Traktor
 *     imports.
 *   - Key comes as musical notation (e.g. "Cm", "F#") — we pass through as
 *     Camelot-style if it looks like Camelot ("8A"), otherwise leave raw
 *     and let the user re-tag if they want strict interop.
 */

// Musical-key → Camelot lookup. Extend as needed; unmatched keys pass through raw.
const CAMELOT_FROM_MUSICAL = {
  "Cm":"5A","C#m":"12A","Dbm":"12A","Dm":"7A","D#m":"2A","Ebm":"2A","Em":"9A",
  "Fm":"4A","F#m":"11A","Gbm":"11A","Gm":"6A","G#m":"1A","Abm":"1A","Am":"8A",
  "A#m":"3A","Bbm":"3A","Bm":"10A",
  "C":"8B","C#":"3B","Db":"3B","D":"10B","D#":"5B","Eb":"5B","E":"12B",
  "F":"7B","F#":"2B","Gb":"2B","G":"9B","G#":"4B","Ab":"4B","A":"11B",
  "A#":"6B","Bb":"6B","B":"1B",
};

const basename = (p) => (p || "").split(/[/\\]/).pop() || "";

/**
 * Parse a VirtualDJ database.xml text blob.
 * @param {string} text
 * @returns {{ crates: Array<{ name: string, tracks: Array }> }}
 */
export function parseVirtualDJXML(text) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Malformed XML — this doesn't look like a VirtualDJ database.xml.");
  }
  if (!doc.querySelector("VirtualDJ_Database")) {
    throw new Error("Missing <VirtualDJ_Database> root — this isn't a VirtualDJ export.");
  }

  const tracks = [];
  for (const s of doc.querySelectorAll("VirtualDJ_Database > Song")) {
    const filePath = s.getAttribute("FilePath") || "";
    const tags = s.querySelector(":scope > Tags");
    const infos = s.querySelector(":scope > Infos");
    const scan = s.querySelector(":scope > Scan");

    const title = tags?.getAttribute("Title") || basename(filePath).replace(/\.[^.]+$/, "");
    const artist = tags?.getAttribute("Author") || "";
    const rawKey = tags?.getAttribute("Key") || "";
    const key = /^\d{1,2}[AB]$/.test(rawKey) ? rawKey : (CAMELOT_FROM_MUSICAL[rawKey] || null);
    const bpm = parseFloat(scan?.getAttribute("Bpm") || tags?.getAttribute("Bpm") || "0") || null;

    tracks.push({
      artist,
      title,
      duration_seconds: parseFloat(infos?.getAttribute("SongLength") || "0") || null,
      bpm,
      key,
      genre: tags?.getAttribute("Genre") || null,
      file_path: filePath,
      filename: basename(filePath),
      hot_cues: Array.from(s.querySelectorAll(":scope > Poi"))
        .filter(p => (p.getAttribute("Type") || "").toLowerCase() === "cue")
        .filter(p => {
          const n = parseInt(p.getAttribute("Num") ?? "-1", 10);
          return n >= 0 && n <= 7;
        })
        .slice(0, 8)
        .map(p => ({
          name: p.getAttribute("Name") || "",
          start_seconds: parseFloat(p.getAttribute("Pos") || "0") || 0,
          num: parseInt(p.getAttribute("Num") ?? "0", 10),
        }))
        .sort((a, b) => a.num - b.num),
    });
  }

  if (!tracks.length) return { crates: [] };
  return { crates: [{ name: "VirtualDJ Library", tracks }] };
}
